/**
 * AdminSatisOnay.jsx
 *
 * Admin/superadmin profil onay kuyruğu — danışmanların "satışa hazır"
 * işaretlediği lead'ler için, admin müşteriyi telefonla arayıp sözlü onay
 * aldıktan sonra öğrenci profilini oluşturur/reddeder.
 *
 * Route: /admin/satis-onay
 * Backend: routes/satis_onay.py (GET/POST /api/admin/satis-talepleri/...)
 *
 * Onayda YALNIZCA öğrenci profili (kullanıcı kaydı + giriş bilgileri)
 * oluşturulur — kurs seçimi, fiyatlandırma ve satış (muhasebe kaydı) bu
 * ekranda YAPILMAZ, danışman kendi ekranından sonradan tamamlar.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import { fmtMoney } from '../utils/money';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  BadgeCheck, CheckCircle2, XCircle, RefreshCw, Phone, User,
  Clock, AlertTriangle, KeyRound, Copy, Loader2, Lightbulb, Mail,
  ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LBL_CLS = 'text-[11.5px] font-bold uppercase tracking-wide text-[var(--crm-muted-2)] mb-1 block';
const INPUT_CLS = 'w-full px-3 py-2 rounded-lg border text-sm font-semibold bg-white focus:outline-none focus:ring-2';
const INPUT_STYLE = { borderColor: 'var(--crm-input-border)', color: 'var(--crm-navy-text)' };

const DURUM_TABS = [
  { value: 'bekliyor', trLabel: 'Onay Bekliyor', enLabel: 'Pending' },
  { value: 'onay_hata', trLabel: 'Hata', enLabel: 'Error' },
  { value: 'islemde', trLabel: 'İşlemde', enLabel: 'In Progress' },
  { value: 'onaylandi', trLabel: 'Profil Oluşturuldu', enLabel: 'Profile Created' },
  { value: 'reddedildi', trLabel: 'Reddedildi', enLabel: 'Rejected' },
  { value: '', trLabel: 'Tümü', enLabel: 'All' },
];

// Kart üstünde/kuyrukta gösterilen durum rozeti — 'bekliyor' halinde form zaten
// bağlamı verdiği için görsel gürültü yaratmasın diye render edilmez (çağıran taraf karar verir).
const STATUS_META = {
  bekliyor: {
    trLabel: 'Onay Bekliyor', enLabel: 'Pending', Icon: Clock,
    bg: 'rgba(201,162,39,0.12)', color: 'var(--crm-gold-dark)',
  },
  onay_hata: {
    trLabel: 'Hata — Tekrar Denenebilir', enLabel: 'Error — Retryable', Icon: AlertTriangle,
    bg: 'rgba(160,61,44,0.10)', color: 'var(--crm-danger)',
  },
  islemde: {
    trLabel: 'İşlemde', enLabel: 'In Progress', Icon: Loader2,
    bg: 'var(--crm-cream)', color: 'var(--crm-muted)',
  },
  onaylandi: {
    trLabel: 'Profil Oluşturuldu', enLabel: 'Profile Created', Icon: CheckCircle2,
    bg: 'rgba(29,138,76,0.10)', color: 'var(--crm-success)',
  },
  reddedildi: {
    trLabel: 'Reddedildi', enLabel: 'Rejected', Icon: XCircle,
    bg: 'rgba(160,61,44,0.10)', color: 'var(--crm-danger)',
  },
};

function StatusPill({ durum, lang }) {
  const meta = STATUS_META[durum];
  if (!meta) return null;
  const Icon = meta.Icon;
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[11px] font-bold flex items-center gap-1"
      style={{ backgroundColor: meta.bg, color: meta.color }}
    >
      <Icon size={11} className={durum === 'islemde' ? 'animate-spin' : ''} />
      {lang === 'tr' ? meta.trLabel : meta.enLabel}
    </span>
  );
}

// "Ders eklenmedi" uyarı rozeti — yalnızca durum 'onaylandi' VE backend'in
// additive 'ders_eklendi' alanı false döndüğünde render edilir (bkz. İŞ 2).
// Renk tek başına bilgi taşımasın diye ikon + metin birlikte kullanılır.
function DersEklenmediBadge({ tr }) {
  return (
    <span
      className="px-2 py-0.5 rounded-full text-[11px] font-bold flex items-center gap-1"
      style={{ backgroundColor: 'rgba(201,162,39,0.12)', color: 'var(--crm-gold-dark)', border: '1px solid var(--crm-gold)' }}
    >
      <AlertTriangle size={11} /> {tr('Ders eklenmedi', 'No courses added')}
    </span>
  );
}

const PAYMENT_METHOD_OPTIONS = [
  { value: 'kredi_karti', tr: 'Kredi Kartı', en: 'Credit Card' },
  { value: 'havale', tr: 'Havale / IBAN', en: 'Bank Transfer / IBAN' },
  { value: 'nakit', tr: 'Nakit', en: 'Cash' },
];

// Danışmanın "Satışa Hazır İşaretle" modalındaki ödeme tercihi (bkz.
// LeadDetayModal submitSatisTalebi, components/crm/DanismanCockpitShared.jsx
// — eskiden AdminAssignedLeads.jsx'teydi, Faz 6'da taşındı) — bu yalnızca
// bilgi amaçlıdır, admin'in girdiği bir karar DEĞİLDİR (admin artık satış
// planı girmiyor, bkz. TalepCard "Danışman Önerisi" kutusu).
const ODEME_TERCIHI_LABEL = {
  pesin: { tr: 'Peşin', en: 'Cash' },
  taksit: { tr: 'Taksit', en: 'Installment' },
  bilinmiyor: { tr: 'Bilinmiyor', en: 'Unknown' },
};

function fmtDate(d, lang) {
  if (!d) return '-';
  try { return new Date(d).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-GB', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return d; }
}

/** courses listesinden course_id -> title map'i (raw slug sızıntısını önlemek için). */
function courseTitle(courseId, courses) {
  if (!courseId) return '-';
  if (courseId === 'multiple') return 'Karma/Peşinat';
  const c = courses.find((x) => x.course_id === courseId);
  return c ? c.title : courseId;
}

// ─── Sonuç modalı (onay başarılı) ───────────────────────────────────────────
function ApproveResultModal({ result, tr, toast, onClose }) {
  if (!result) return null;
  const tempPassword = result.temp_password;

  const copyPassword = () => {
    if (!tempPassword) return;
    navigator.clipboard?.writeText(tempPassword)
      .then(() => toast?.success(tr('Şifre kopyalandı', 'Password copied')))
      .catch(() => {});
  };

  return (
    <Dialog open={!!result} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-bold" style={{ color: 'var(--crm-navy-text)' }}>
            <CheckCircle2 size={20} style={{ color: 'var(--crm-success)' }} />
            {tr('Profil Oluşturuldu', 'Profile Created')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 text-sm" style={{ color: 'var(--crm-navy-text)' }}>
          <p>{tr(
            'Öğrenci profili oluşturuldu. Kurs seçimi ve satış işlemi danışman tarafından ayrıca tamamlanacak.',
            'The student profile was created. Course selection and the sale will be completed separately by the consultant.',
          )}</p>
          <p className="font-mono text-xs" style={{ color: 'var(--crm-muted)' }}>{tr('Öğrenci ID', 'Student ID')}: {result.user_id}</p>
          {result.sentetik_email === true && (
            <p className="text-xs font-semibold p-2 rounded-lg" style={{ backgroundColor: 'rgba(201,162,39,0.10)', border: '1px solid var(--crm-gold)', color: 'var(--crm-gold-dark)' }}>
              {tr(
                'Bu öğrencinin e-postası yoktu — hesap sentetik bir adresle açıldı, giriş bilgilerini yalnızca telefonla iletebilirsiniz.',
                'This student had no email — the account was created with a synthetic address, you can only deliver login details by phone.',
              )}
            </p>
          )}
          {tempPassword && (
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(201,162,39,0.10)', border: '1px solid var(--crm-gold)' }}>
              <p className="font-bold text-xs uppercase tracking-wide flex items-center gap-1.5 mb-1.5" style={{ color: 'var(--crm-gold-dark)' }}>
                <KeyRound size={13} /> {tr('Geçici Şifre', 'Temporary Password')}
              </p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-base font-bold flex-1 px-2 py-1 rounded bg-white" style={{ color: 'var(--crm-navy-text)', border: '1px solid var(--crm-input-border)' }}>
                  {tempPassword}
                </span>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="p-2 rounded-lg text-white flex-shrink-0"
                  style={{ backgroundColor: 'var(--crm-navy)' }}
                  title={tr('Kopyala', 'Copy')}
                >
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-[11px] font-semibold mt-1.5" style={{ color: 'var(--crm-danger)' }}>
                {tr('Bu şifre bir daha gösterilmeyecek — öğrenciye iletin.', 'This password will not be shown again — deliver it to the student.')}
              </p>
            </div>
          )}
        </div>
        <div className="flex justify-end pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: 'var(--crm-navy)' }}
          >
            {tr('Tamam', 'OK')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Talep kartı ─────────────────────────────────────────────────────────────
function TalepCard({ talep, courses, lang, tr, authHeaders, toast, onDecided, onApproved }) {
  // 'onay_hata' (retryable hata) icin de ayni onayla akisi kullanilir —
  // backend ayni endpoint'te "islemde" -> ... retry'i destekler.
  const canApprove = talep.durum === 'bekliyor' || talep.durum === 'onay_hata';
  const isLocked = talep.durum === 'islemde';
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const [sesliOnayNotu, setSesliOnayNotu] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [gerekce, setGerekce] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const lead = talep.lead_ozet || {};
  const gerekceValid = gerekce.trim().length >= 5;

  const oe = talep.onerilen_egitim;
  const oeCourseIds = Array.isArray(oe?.course_ids) ? oe.course_ids : [];

  const confirmReasonId = `musteri-dogrula-reason-${talep.talep_id}`;

  const doApprove = async () => {
    setApproving(true);
    try {
      const body = { musteri_onayi_dogrulandi: true };
      const not = sesliOnayNotu.trim();
      if (not) body.sesli_onay_notu = not;
      const r = await axios.post(`${API}/admin/satis-talepleri/${talep.talep_id}/onayla`, body, { headers: authHeaders() });
      toast.success(tr('Öğrenci profili oluşturuldu', 'Student profile created'));
      onApproved(r.data);
      onDecided();
    } catch (err) {
      const errCode = getApiErrorCode(err);
      if (errCode === 'talep_islemde_veya_kapali') {
        toast.error(tr('Bu talep şu anda işleniyor veya kapatılmış.', 'This request is currently being processed or already closed.'));
        onDecided();
      } else {
        toast.error(getApiErrorMessage(err, lang));
        onDecided();
      }
    } finally {
      setApproving(false);
      setConfirmOpen(false);
    }
  };

  const doReject = async () => {
    if (!gerekceValid) return;
    setRejecting(true);
    try {
      await axios.post(`${API}/admin/satis-talepleri/${talep.talep_id}/reddet`, { gerekce: gerekce.trim() }, { headers: authHeaders() });
      toast.success(tr('Talep reddedildi', 'Request rejected'));
      setRejectOpen(false);
      onDecided();
    } catch (err) {
      const errCode = getApiErrorCode(err);
      if (errCode === 'talep_donusturulmus_reddedilemez') {
        toast.error(tr(
          'Öğrenci kaydı oluşmuş — talep reddedilemez, tekrar deneyin.',
          'A student record already exists — the request cannot be rejected, please retry.',
        ));
        setRejectOpen(false);
        onDecided();
      } else if (errCode === 'talep_karara_baglanmis') {
        toast.error(tr('Talep zaten karara bağlanmış.', 'This request has already been decided.'));
        setRejectOpen(false);
        onDecided();
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setRejecting(false);
    }
  };

  return (
    <div
      className="bg-white p-4"
      style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}
    >
      {/* Üst satır */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-base flex items-center gap-1.5" style={{ color: 'var(--crm-navy-text)' }}>
            <User size={16} style={{ color: 'var(--crm-navy)' }} />
            {lead.ad_soyad || talep.lead_id}
          </span>
          {lead.telefon && (
            <span className="text-xs font-semibold flex items-center gap-1" style={{ color: 'var(--crm-muted)' }}>
              <Phone size={12} /> {lead.telefon}
            </span>
          )}
          <span className="px-2 py-0.5 rounded-full text-[11px] font-bold" style={{ backgroundColor: 'var(--crm-cream)', color: 'var(--crm-navy)', border: '1px solid var(--crm-card-border)' }}>
            {talep.danisman_adi || '-'}
          </span>
          <StatusPill durum={talep.durum} lang={lang} />
          {talep.durum === 'onaylandi' && talep.ders_eklendi === false && <DersEklenmediBadge tr={tr} />}
        </div>
        <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color: 'var(--crm-muted-2)' }}>
          <Clock size={12} /> {tr('Talep', 'Request')} · {fmtDate(talep.created_at, lang)}
        </span>
      </div>

      {/* E-postasız lead uyarısı — alan backend'den henüz gelmiyorsa (undefined)
          hiç render edilmez (geriye uyumlu, client-side maskeleme değil). */}
      {talep.lead_email_yok === true && (
        <div
          className="mb-3 px-3 py-2 rounded-lg text-xs font-semibold flex items-start gap-2"
          style={{ backgroundColor: 'rgba(201,162,39,0.10)', border: '1px solid var(--crm-gold)', color: 'var(--crm-gold-dark)' }}
        >
          <Mail size={14} className="flex-shrink-0 mt-0.5" />
          <span>
            {tr(
              "Bu lead'in e-postası yok — öğrenci hesabı sentetik adresle açılır, giriş bilgileri iletilemez.",
              "This lead has no email — the student account will be created with a synthetic address and login details cannot be delivered.",
            )}
          </span>
        </div>
      )}

      {/* Danışman önerisi — bilgi amaçlı. Admin telefonda konuşurken bağlam
          sağlar; bir karar girdisi DEĞİLDİR (form yok, admin burada hiçbir
          alanı düzenlemez). */}
      {(oeCourseIds.length > 0 || oe?.onerilen_fiyat != null || oe?.odeme_tercihi) && (
        <div
          className="mb-3 px-3 py-2 rounded-lg text-sm"
          style={{ backgroundColor: 'rgba(46,107,143,0.06)', border: '1px solid rgba(46,107,143,0.2)' }}
        >
          <p className="font-bold text-xs uppercase tracking-wide flex items-center gap-1.5 mb-1" style={{ color: 'var(--crm-info)' }}>
            <Lightbulb size={13} /> {tr('Danışman Önerisi — bilgi amaçlı', 'Consultant Suggestion — for reference only')}
          </p>
          {oeCourseIds.length > 0 && (
            <p style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Eğitim(ler)', 'Course(s)')}: {oeCourseIds.map((cid, i) => courseTitle(cid, courses) === cid ? (oe.course_titles?.[i] || cid) : courseTitle(cid, courses)).join(', ')}
            </p>
          )}
          {oe?.onerilen_fiyat != null && (
            <p style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Önerilen Fiyat', 'Suggested Price')}: {fmtMoney(oe.onerilen_fiyat)}
            </p>
          )}
          {oe?.odeme_tercihi && (
            <p style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Ödeme Tercihi', 'Payment Preference')}: {(ODEME_TERCIHI_LABEL[oe.odeme_tercihi]?.[lang === 'tr' ? 'tr' : 'en']) || oe.odeme_tercihi}
            </p>
          )}
          <p className="text-[10.5px] font-semibold mt-1" style={{ color: 'var(--crm-muted-2)' }}>
            {tr('Bu bilgiler yalnızca danışmanın önerisidir — kurs ve fiyat kararı burada verilmez.', 'This information is only the consultant’s suggestion — the course and price decision is not made here.')}
          </p>
        </div>
      )}

      {/* Danışman notu */}
      {talep.danisman_notu && (
        <div
          className="mb-3 px-3 py-2 rounded-lg text-sm italic"
          style={{ backgroundColor: '#FAFAF8', borderLeft: '3px solid var(--crm-gold)', color: 'var(--crm-navy-text)' }}
        >
          "{talep.danisman_notu}"
        </div>
      )}

      {/* Hata uyarısı (retry bağlamı) */}
      {talep.durum === 'onay_hata' && (
        <div
          className="mb-3 px-3 py-2 rounded-lg text-sm flex items-start gap-2"
          style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)', color: 'var(--crm-danger)' }}
        >
          <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-bold">{tr('İşlem başarısız oldu', 'The action failed')}</p>
            {talep.hata_mesaji && <p className="text-xs mt-0.5">{talep.hata_mesaji}</p>}
            <p className="text-xs mt-0.5" style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Tekrar onaylayarak yeniden deneyebilirsiniz.', 'You can try again by approving once more.')}
            </p>
          </div>
        </div>
      )}

      {/* onay_hata + donusturulen_user_id dolu: reddedilemez, sadece retry — bilgi notu */}
      {talep.durum === 'onay_hata' && talep.donusturulen_user_id && (
        <div
          className="mb-3 px-3 py-2 rounded-lg text-xs font-semibold flex items-start gap-2"
          style={{ backgroundColor: 'rgba(46,107,143,0.08)', border: '1px solid rgba(46,107,143,0.25)', color: 'var(--crm-info)' }}
        >
          <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
          <span>
            {tr('Öğrenci kaydı oluştu — Tekrar Dene ile tamamlayın.', 'The student record was created — complete it with Retry.')}
          </span>
        </div>
      )}

      {isLocked ? (
        <div
          className="flex items-center gap-2 text-sm font-semibold p-3 rounded-lg"
          style={{ backgroundColor: 'var(--crm-cream)', color: 'var(--crm-muted)' }}
        >
          <Loader2 size={15} className="animate-spin" />
          {tr('Bu talep şu anda işleniyor — başka bir istek tarafından kilitlendi.', 'This request is currently being processed — locked by another request.')}
        </div>
      ) : canApprove ? (
        <>
          {/* Görüşme notu (opsiyonel) */}
          <div className="mb-3">
            <label className={LBL_CLS} htmlFor={`sesli-onay-notu-${talep.talep_id}`}>
              {tr('Görüşme Notu', 'Call Note')} <span className="font-normal normal-case" style={{ color: 'var(--crm-muted-2)' }}>({tr('opsiyonel', 'optional')})</span>
            </label>
            <textarea
              id={`sesli-onay-notu-${talep.talep_id}`}
              value={sesliOnayNotu}
              onChange={(e) => setSesliOnayNotu(e.target.value)}
              rows={2}
              className={`${INPUT_CLS} resize-none`}
              style={INPUT_STYLE}
              placeholder={tr('Görüşmeyle ilgili serbest not (opsiyonel)...', 'Free-form note about the call (optional)...')}
            />
          </div>

          {/* "Onayladığınızda" bilgi kutusu */}
          <div
            className="mb-3 px-3 py-2.5 rounded-lg text-xs"
            style={{ backgroundColor: 'rgba(46,107,143,0.06)', border: '1px solid rgba(46,107,143,0.2)' }}
          >
            <p className="font-bold uppercase tracking-wide flex items-center gap-1.5 mb-1.5" style={{ color: 'var(--crm-info)' }}>
              <Lightbulb size={13} /> {tr('Onayladığınızda', 'When you approve')}
            </p>
            <ul className="space-y-1 list-disc pl-4" style={{ color: 'var(--crm-navy-text)' }}>
              <li>{tr("Öğrencinin profili ve giriş bilgileri (geçici şifre) oluşturulur.", "The student's profile and login credentials (temporary password) are created.")}</li>
              <li>{tr("Kurs seçimi ve satış işlemi bu adımda yapılmaz — danışman tarafından ayrıca tamamlanır.", "Course selection and the sale are not completed in this step — the consultant will finish them separately.")}</li>
            </ul>
          </div>

          {/* Müşteri doğrulama checkbox'ı */}
          <div className="mb-3 flex items-start gap-2">
            <Checkbox
              id={`musteri-dogrula-${talep.talep_id}`}
              checked={customerConfirmed}
              onCheckedChange={(v) => setCustomerConfirmed(v === true)}
              aria-describedby={!customerConfirmed ? confirmReasonId : undefined}
            />
            <div>
              <label htmlFor={`musteri-dogrula-${talep.talep_id}`} className="text-xs font-semibold cursor-pointer" style={{ color: 'var(--crm-navy-text)' }}>
                {tr('Müşteriyle telefonda görüşüldü ve sözlü onay alındı.', 'The customer was called on the phone and verbal confirmation was obtained.')}
              </label>
              {!customerConfirmed && (
                <p id={confirmReasonId} aria-live="polite" className="text-[11px] font-bold mt-0.5 flex items-center gap-1" style={{ color: 'var(--crm-danger)' }}>
                  <ShieldCheck size={11} /> {tr('Onaylamak için önce müşteri doğrulamasını işaretleyin.', 'Check customer confirmation before approving.')}
                </p>
              )}
            </div>
          </div>

          {/* Aksiyonlar */}
          <div className="flex flex-wrap justify-end gap-2">
            {(talep.durum === 'bekliyor' || (talep.durum === 'onay_hata' && !talep.donusturulen_user_id)) && (
              <button
                onClick={() => setRejectOpen(true)}
                className="px-4 py-2 rounded-lg text-sm font-bold border-2 flex items-center gap-1.5"
                style={{ borderColor: 'var(--crm-danger)', color: 'var(--crm-danger)', backgroundColor: 'white' }}
              >
                <XCircle size={15} /> {tr('Reddet', 'Reject')}
              </button>
            )}
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={!customerConfirmed}
              aria-describedby={!customerConfirmed ? confirmReasonId : undefined}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white flex items-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: 'var(--crm-success-btn)' }}
            >
              <CheckCircle2 size={15} />
              {talep.durum === 'onay_hata' ? tr('Tekrar Dene', 'Retry') : tr('Onayla', 'Approve')}
            </button>
          </div>
        </>
      ) : (
        <div className="text-sm" style={{ color: 'var(--crm-navy-text)' }}>
          {talep.durum === 'onaylandi' && (
            <div className="space-y-1">
              {talep.satis_plani ? (
                <>
                  <p className="font-bold">{tr('Onaylanan Satış Planı', 'Approved Sale Plan')}</p>
                  {(talep.satis_plani.egitimler || []).map((e, i) => (
                    <p key={i} style={{ color: 'var(--crm-muted)' }}>
                      {courseTitle(e.course_id, courses)} — {fmtMoney(e.liste_fiyati)} ({e.tur === 'ana_egitim' ? tr('Ana', 'Main') : tr('Ek', 'Additional')})
                    </p>
                  ))}
                  <p style={{ color: 'var(--crm-muted)' }}>
                    {tr('Ödeme', 'Payment')}: {PAYMENT_METHOD_OPTIONS.find((o) => o.value === talep.satis_plani.payment_method)?.[lang === 'tr' ? 'tr' : 'en'] || talep.satis_plani.payment_method}
                  </p>
                </>
              ) : (
                <p style={{ color: 'var(--crm-muted)' }}>
                  {tr('Profil oluşturuldu — kurs seçimi ve satış danışman tarafından tamamlanacak.', 'Profile created — course selection and the sale will be completed by the consultant.')}
                </p>
              )}
              <p style={{ color: 'var(--crm-muted)' }}>
                {tr('Karar', 'Decision')}: {fmtDate(talep.karar_tarihi, lang)}
              </p>
            </div>
          )}
          {talep.durum === 'reddedildi' && (
            <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.2)' }}>
              <p className="font-bold" style={{ color: 'var(--crm-danger)' }}>{tr('Red Gerekçesi', 'Rejection Reason')}</p>
              <p style={{ color: 'var(--crm-navy-text)' }}>{talep.red_gerekcesi}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--crm-muted-2)' }}>{fmtDate(talep.karar_tarihi, lang)}</p>
            </div>
          )}
        </div>
      )}

      {/* Onay öncesi doğrulama dialog'u — "Profili oluştur?" */}
      <Dialog open={confirmOpen} onOpenChange={(o) => { if (!o && !approving) setConfirmOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Profili oluştur?', 'Create profile?')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm" style={{ color: 'var(--crm-navy-text)' }}>
            {tr(
              'Bu işlem yalnızca öğrenci profilini oluşturur ve giriş bilgilerini üretir. Kurs seçimi ve satış işlemi danışman tarafından ayrıca tamamlanacaktır.',
              'This action only creates the student profile and generates login credentials. Course selection and the sale will be completed separately by the consultant.',
            )}
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setConfirmOpen(false)}
              disabled={approving}
              className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100 disabled:opacity-50"
              style={{ color: 'var(--crm-navy-text)' }}
            >
              {tr('Vazgeç', 'Cancel')}
            </button>
            <button
              onClick={doApprove}
              disabled={approving}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 flex items-center gap-1.5"
              style={{ backgroundColor: 'var(--crm-success-btn)' }}
            >
              <CheckCircle2 size={15} /> {approving ? '...' : tr('Profili Oluştur', 'Create Profile')}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Red gerekçesi dialog */}
      <Dialog open={rejectOpen} onOpenChange={(o) => { if (!o && !rejecting) setRejectOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Talebi Reddet', 'Reject Request')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs" style={{ color: 'var(--crm-muted)' }}>{lead.ad_soyad || talep.lead_id}</p>
          <div>
            <label className={LBL_CLS}>{tr('Gerekçe', 'Reason')} <span style={{ color: 'var(--crm-danger)' }}>*</span></label>
            <textarea
              value={gerekce}
              onChange={(e) => setGerekce(e.target.value)}
              rows={3}
              className={`${INPUT_CLS} resize-none`}
              style={INPUT_STYLE}
              placeholder={tr('Red gerekçesini girin (en az 5 karakter)...', 'Enter rejection reason (min 5 characters)...')}
            />
            {gerekce.length > 0 && !gerekceValid && (
              <p className="text-[10px] font-bold mt-1" style={{ color: 'var(--crm-danger)' }}>
                {tr('Gerekçe en az 5 karakter olmalı', 'Reason must be at least 5 characters')}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setRejectOpen(false)} disabled={rejecting} className="px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-100" style={{ color: 'var(--crm-navy-text)' }}>
              {tr('Vazgeç', 'Cancel')}
            </button>
            <button
              onClick={doReject}
              disabled={rejecting || !gerekceValid}
              className="px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 flex items-center gap-1.5"
              style={{ backgroundColor: 'var(--crm-danger)' }}
            >
              <XCircle size={15} /> {rejecting ? '...' : tr('Reddet', 'Reject')}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Ana sayfa ────────────────────────────────────────────────────────────────
export default function AdminSatisOnay() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [durum, setDurum] = useState('bekliyor');
  const [talepler, setTalepler] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [courses, setCourses] = useState([]);
  const [approveResult, setApproveResult] = useState(null);
  const [onlyMissingCourses, setOnlyMissingCourses] = useState(false);

  const fetchTalepler = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const p = new URLSearchParams();
      if (durum) p.set('durum', durum);
      p.set('page_size', '50');
      const r = await axios.get(`${API}/admin/satis-talepleri?${p.toString()}`, { headers: authHeaders() });
      setTalepler(r.data.talepler || []);
      setTotal(r.data.total || 0);
    } catch (err) {
      setError(true);
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durum, lang]);

  useEffect(() => { fetchTalepler(); }, [fetchTalepler]);

  useEffect(() => {
    axios.get(`${API}/courses?limit=100`).then((r) => setCourses(r.data.courses || [])).catch(() => {});
  }, []);

  // 'ders_eklendi' yalnızca durum 'onaylandi' iken anlamlıdır (backend diğer
  // durumlarda null döner) — filtre bu yüzden başka sekmelerde etkisiz kalır.
  const visibleTalepler = onlyMissingCourses
    ? talepler.filter((t) => t.ders_eklendi === false)
    : talepler;

  return (
    <AdminLayout>
      <div className="-m-4 lg:-m-6 p-4 lg:p-6 min-h-full" style={{ backgroundColor: 'var(--crm-cream)' }}>
        {/* Başlık */}
        <div className="mb-5">
          <nav className="mb-1 text-xs font-semibold" style={{ color: 'var(--crm-muted-2)' }}>
            Admin <span className="mx-1">/</span> {tr('Profil Onayları', 'Profile Approvals')}
          </nav>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--crm-navy-text)' }}>
            <BadgeCheck size={22} style={{ color: 'var(--crm-navy)' }} />
            {tr('Profil Onay Kuyruğu', 'Profile Approval Queue')}
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--crm-muted)' }}>
            {tr(
              "Danışmanların satışa hazır işaretlediği lead'leri telefonla arayıp sözlü onay aldıktan sonra öğrenci profilini oluşturun veya talebi reddedin. Kurs seçimi ve satış işlemi danışman tarafından ayrıca tamamlanır.",
              'Call the leads consultants marked ready, get verbal confirmation, then create the student profile or reject the request. Course selection and the sale are completed separately by the consultant.',
            )}
          </p>
        </div>

        {/* Durum sekmeleri */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {DURUM_TABS.map((t) => (
            <button
              key={t.value || 'all'}
              onClick={() => setDurum(t.value)}
              className="px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors"
              style={durum === t.value
                ? { backgroundColor: 'var(--crm-navy)', color: 'white', borderColor: 'var(--crm-navy)' }
                : { backgroundColor: 'white', color: 'var(--crm-navy-text)', borderColor: 'var(--crm-card-border)' }}
            >
              {lang === 'tr' ? t.trLabel : t.enLabel}
            </button>
          ))}
          <span className="text-xs font-semibold ml-1" style={{ color: 'var(--crm-muted-2)' }}>
            {total} {tr('talep', 'requests')}
          </span>
          {(durum === 'onaylandi' || durum === '') && (
            <label className="flex items-center gap-1.5 text-xs font-bold cursor-pointer ml-2" style={{ color: 'var(--crm-navy-text)' }}>
              <Checkbox checked={onlyMissingCourses} onCheckedChange={(v) => setOnlyMissingCourses(v === true)} />
              {tr('Sadece ders eklenmeyenler', 'Only missing courses')}
            </label>
          )}
        </div>

        {/* Hata banner */}
        {error && !loading && (
          <div
            className="mb-4 p-3 rounded-xl flex items-center justify-between gap-3"
            style={{ backgroundColor: 'rgba(160,61,44,0.06)', border: '1px solid rgba(160,61,44,0.25)' }}
          >
            <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--crm-danger)' }}>
              <AlertTriangle size={16} /> {tr('Bağlantı koptu — tekrar dene', 'Connection lost — try again')}
            </span>
            <button
              onClick={fetchTalepler}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white flex items-center gap-1"
              style={{ backgroundColor: 'var(--crm-danger)' }}
            >
              <RefreshCw size={13} /> {tr('Tekrar Dene', 'Retry')}
            </button>
          </div>
        )}

        {/* Skeleton */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white p-4" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
                <Skeleton className="h-5 w-1/3 mb-3" />
                <Skeleton className="h-4 w-2/3 mb-2" />
                <Skeleton className="h-16 w-full" />
              </div>
            ))}
          </div>
        )}

        {/* Boş durum */}
        {!loading && !error && visibleTalepler.length === 0 && (
          <div className="bg-white p-8 text-center" style={{ borderRadius: 14, border: '1px solid var(--crm-card-border)' }}>
            <BadgeCheck size={32} className="mx-auto mb-2" style={{ color: 'var(--crm-muted-2)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--crm-muted)' }}>
              {onlyMissingCourses
                ? tr('Ders eklenmemiş kayıt yok.', 'No records with missing courses.')
                : durum === 'bekliyor'
                  ? tr('Onay bekleyen talep yok.', 'No pending requests.')
                  : tr('Bu durumda talep yok.', 'No requests in this state.')}
            </p>
          </div>
        )}

        {/* Kuyruk */}
        {!loading && !error && visibleTalepler.length > 0 && (
          <div className="space-y-3">
            {visibleTalepler.map((t) => (
              <TalepCard
                key={t.talep_id}
                talep={t}
                courses={courses}
                lang={lang}
                tr={tr}
                authHeaders={authHeaders}
                toast={toast}
                onDecided={fetchTalepler}
                onApproved={setApproveResult}
              />
            ))}
          </div>
        )}
      </div>

      <ApproveResultModal result={approveResult} tr={tr} toast={toast} onClose={() => setApproveResult(null)} />
    </AdminLayout>
  );
}
